/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import {
  Button,
  Form,
  Select,
  Row,
  Col,
  Space,
  Input,
  InputNumber,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions, TResponseModel } from "../../../types/config";
import {
  APPROACH,
  eHTTPStatusCode,
  ePrivileges,
  eREPORTTYPE,
  eResultCode,
  MONTHS,
  QUARTERS,
  REPORTDURATION,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TReportPayload } from "../../../types";
import {
  GetAMCSummaryReport,
  GetCustomerList,
  GetCustMappedPartnerList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import { STAKE_HOLDERS, UNAUTHORIZEDREQUEST } from "../../../utils/constants";
import AuthUtil from "../../../utils/auth";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import { useWatch } from "antd/es/form/Form";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

export default function SummaryReportForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [servicePartnerOptions, setServicePartnerOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { label: number; value: number }[]
  >([]);
  const [fromDate, setFromDate] = useState(null);
  const [activeReportType, setActiveReportType] = useState<number>(
    eREPORTTYPE.AMCSUMMARYREPORT
  );
  const [reportDuration, setReportDuration] = useState<number>();
  const { onShowToast } = useToast();
  const context: any = useContext(UserContext);
  const { post } = useFetch();
  const approachValue = useWatch("calculationApproach", form);
  useEffect(() => {
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldValue("customerId", context?.user?.orgId);
      form.setFieldValue("customerName", context?.user?.orgName);
      getCustomerMappedServicePartners(context?.user?.orgId);
    } else if (
      context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER
    ) {
      form.setFieldValue("servicePartnerId", context?.user?.orgId);
      form.setFieldValue("servicePartnerName", context?.user?.orgName);
    }
  }, [context?.user]);

  useEffect(() => {
    setYearOptions(generateYearOptions());
    form.setFieldValue("reportType", eREPORTTYPE.AMCSUMMARYREPORT);
  }, []);

  const getCustomerMappedServicePartners = async (customerId: string) => {
    try {
      const payload = {
        data: {
          customerId,
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetCustMappedPartnerList,
        payload
      );

      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setServicePartnerOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching mapped partners:", error);
    }
  };

  // function to generate the year option
  function generateYearOptions() {
    const startYear = 2024;
    const currentYear = new Date().getFullYear(); // Get the current year
    const yearArray = [];

    for (let year = startYear; year <= currentYear; year++) {
      yearArray.push({ label: year, value: year });
    }

    return yearArray;
  }

  // Handle on submit event
  const onFinish = async (values: TReportPayload) => {
    try {
      const payload = {
        data: {
          ...values,
          reportType: activeReportType,
          approachTwoParams:
            values?.calculationApproach === APPROACH.APPROACH2
              ? [
                  {
                    gyro: 1,
                    ups: 1,
                    percentage: values?.gyuy,
                  },
                  {
                    gyro: 1,
                    ups: 0,
                    percentage: values?.gyun,
                  },
                  {
                    gyro: 0,
                    ups: 1,
                    percentage: values?.gnuy,
                  },
                  {
                    gyro: 0,
                    ups: 0,
                    percentage: values?.gnun,
                  },
                ]
              : [],
        },
      };
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetAMCSummaryReport}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Ensure JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            ...payload,
          }), // Convert body to JSON string
        }
      );
      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Extract content-disposition header to get the file name
        const contentDisposition = response.headers.get("Content-Disposition");
        let fileName = `${"AMC_Summary_Report"}.xlsx`; // Default file name
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "Report generated successfully!",
        });
        props?.onCloseDrawer();
        props?.onRefreshList();
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Error while downloading report, please try again later.",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="reportForm"
        autoComplete="off"
        onFinish={onFinish}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if (changedValues.fromDate) {
            setFromDate(changedValues.fromDate); // Update fromDate when changed
          }
        }}
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  Generate AMC Report
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <CustomDropdown
                  isDisabled={
                    context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER ||
                    isLoading
                      ? true
                      : false
                  }
                  endPoint={GetCustomerList}
                  value={form.getFieldValue("customerId")}
                  selectedName={form.getFieldValue("customerName")}
                  onChange={(selected: any) => {
                    if (selected?.servicePartners?.length) {
                      form.setFieldValue("customerId", selected.value);
                      form.setFieldValue("customerName", selected.label);
                      form.setFieldValue("servicePartnerId", null);
                      setServicePartnerOptions(
                        Utils.mapDropdownOptions(selected?.servicePartners)
                      );
                    } else {
                      form.setFieldValue("customerId", null);
                      form.setFieldValue("customerName", "");
                      onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.centerTop,
                        content:
                          "Service Partner is not mapped with this customer",
                      });
                    }
                  }}
                  label=""
                  placeHolder="Select customer"
                ></CustomDropdown>
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                name="reportType"
                label="Report Type"
                rules={[
                  { required: true, message: "Please select report type" },
                ]}
              >
                <Select
                  placeholder="Select report type"
                  options={reportTypeOptions}
                  onChange={(value: number) => setActiveReportType(value)}
                />
              </Form.Item>
            </Col> */}

            {/* REPORT DURATION */}
            {activeReportType === eREPORTTYPE.AMCSUMMARYREPORT ? (
              <>
                {/* <Col span={24}>
                  <Form.Item
                    name="servicePartnerId"
                    label="Service Partner"
                    rules={[
                      {
                        required: true,
                        message: "Please choose service partner",
                      },
                    ]}
                  >
                    <Select
                      disabled={
                        context?.user?.stakeHolderName ===
                        STAKE_HOLDERS.SERVICEPARTNER
                      }
                      placeholder="Select Service Partner"
                      options={servicePartnerOptions}
                    />
                  </Form.Item>
                </Col> */}
                <Col span={reportDuration ? 12 : 24}>
                  <Form.Item
                    name="reportDuration"
                    label="Report Duration"
                    rules={[
                      {
                        required: true,
                        message: "Please select report duration",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select report duration"
                      options={[
                        { value: REPORTDURATION.MONTH, label: "Month" },
                        { value: REPORTDURATION.QUARTER, label: "Quarter" },
                        { value: REPORTDURATION.YEAR, label: "Year" },
                      ]}
                      onChange={(value) => setReportDuration(value)}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null}

            {activeReportType === eREPORTTYPE.AMCSUMMARYREPORT &&
            reportDuration === REPORTDURATION.QUARTER ? (
              <Col span={12}>
                <Form.Item
                  name="quarter"
                  label="Quarter"
                  rules={[{ required: true, message: "Please select quarter" }]}
                >
                  <Select
                    placeholder="Select quarter"
                    options={[
                      { value: QUARTERS.Q1, label: "Q1" },
                      { value: QUARTERS.Q2, label: "Q2" },
                      { value: QUARTERS.Q3, label: "Q3" },
                      { value: QUARTERS.Q4, label: "Q4" },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null}

            {activeReportType === eREPORTTYPE.AMCSUMMARYREPORT &&
            reportDuration === REPORTDURATION.MONTH ? (
              <Col span={12}>
                <Form.Item
                  name="month"
                  label="Month"
                  rules={[{ required: true, message: "Please select month" }]}
                >
                  <Select
                    placeholder="Select month"
                    options={[
                      { value: MONTHS.JANUARY, label: "January" },
                      { value: MONTHS.FEBRUARY, label: "February" },
                      { value: MONTHS.MARCH, label: "March" },
                      { value: MONTHS.APRIL, label: "April" },
                      { value: MONTHS.MAY, label: "May" },
                      { value: MONTHS.JUNE, label: "June" },
                      { value: MONTHS.JULY, label: "July" },
                      { value: MONTHS.AUGUST, label: "August" },
                      { value: MONTHS.SEPTEMBER, label: "September" },
                      { value: MONTHS.OCTOBER, label: "October" },
                      { value: MONTHS.NOVEMBER, label: "November" },
                      { value: MONTHS.DECEMBER, label: "December" },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null}

            {activeReportType === eREPORTTYPE.AMCSUMMARYREPORT ? (
              <Col span={reportDuration === REPORTDURATION.YEAR ? 12 : 24}>
                <Form.Item
                  name="year"
                  label="Year"
                  rules={[{ required: true, message: "Please select year" }]}
                >
                  <Select placeholder="Select year" options={yearOptions} />
                </Form.Item>
              </Col>
            ) : null}

            {activeReportType === eREPORTTYPE.AMCSUMMARYREPORT &&
            reportDuration ? (
              <>
                <Col span={12}>
                  <Form.Item
                    name="taxHeading"
                    label="Tax Heading"
                    rules={[
                      { required: true, message: "Please enter tax heading" },
                    ]}
                  >
                    <Input placeholder="Enter tax heading" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="taxPercent"
                    label="Tax Percent"
                    rules={[
                      { required: true, message: "Please enter tax percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter tax percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2} // Allows up to 2 decimal places
                      // formatter={(value) => `${value}%`}
                      // parser={(value) =>
                      //   value?.replace("%", "") as unknown as number
                      // }
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null}

            <Col span={24}>
              <Form.Item
                name="calculationApproach"
                label="Approach"
                rules={[{ required: true, message: "Please select approach" }]}
              >
                <Select
                  placeholder="Select approach"
                  options={[
                    { value: APPROACH.APPROACH1, label: "Approach 1" },
                    { value: APPROACH.APPROACH2, label: "Approach 2" },
                  ]}
                />
              </Form.Item>
            </Col>

            {approachValue === APPROACH.APPROACH2 ? (
              <>
                <Col span={12}>
                  <Form.Item
                    name="gyuy"
                    label="Gyro-Yes, UPS-Yes"
                    rules={[
                      { required: true, message: "Please enter percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="gyun"
                    label="Gyro-Yes, UPS-No"
                    rules={[
                      { required: true, message: "Please enter percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="gnuy"
                    label="Gyro-No, UPS-Yes"
                    rules={[
                      { required: true, message: "Please enter percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="gnun"
                    label="Gyro-No, UPS-No"
                    rules={[
                      { required: true, message: "Please enter percent" },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter percent"
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null}
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              Generate
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
